/**
 * Child-booking identity.
 *
 * A guardian can book on behalf of their child. The booking then carries the ACCOUNT HOLDER (the
 * guardian) in `patient` (enriched by the API gateway from the user record) and the child as a
 * booking-time snapshot: `is_for_child` + `child_name` / `child_surname` / `child_date_of_birth` /
 * `child_gender`.
 *
 * Mirrors how the doctor app splits a child consultation: the CHILD is the person being served,
 * the guardian is only the account holder. Centralised so the dashboard tables, the order
 * management tables and the details pages all resolve the two people the same way.
 *
 * Accepts snake_case and camelCase, because some call sites carry camelCase copies.
 */

/** True when this booking was made by a guardian for their child. */
export const isChildBooking = (booking) =>
  booking?.is_for_child === true || booking?.isForChild === true;

/**
 * The child's display name — given name + surname, the same way an adult's `full_name + surname`
 * is composed. Empty for a normal (self) booking, so a stale snapshot on a self booking can never
 * surface as a child.
 */
export const composeBookingChildName = (booking) => {
  if (!isChildBooking(booking)) return "";
  return [
    booking.child_name ?? booking.childName,
    booking.child_surname ?? booking.childSurname,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
};

/** The child's date of birth / gender snapshot, or null when absent. */
export const getBookingChildDob = (booking) =>
  booking?.child_date_of_birth ?? booking?.childDateOfBirth ?? null;
export const getBookingChildGender = (booking) =>
  booking?.child_gender ?? booking?.childGender ?? null;

/** Whole years between a 'YYYY-MM-DD' date of birth and today, or null when missing/invalid. */
export const ageFromDob = (dob) => {
  if (!dob) return null;
  const parts = String(dob).split("-");
  const birth =
    parts.length >= 3 && parts[0].length === 4
      ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2].slice(0, 2)))
      : new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) years -= 1;
  return years >= 0 ? years : null;
};

/** "male" → "Male". Null-safe. */
export const titleCaseGender = (value) => {
  if (!value) return "";
  const str = String(value);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
